import React from "react";
import { paymentDispatcher } from "../../../actions/book/payment";
import { sendPixelEvent } from "../../../helpers";
import { getExchangedPrice } from "../../../umbilical_hocs";
import * as yup from "yup";
import _ from "lodash";
import { validationSchema } from "./TravellerForms/helpers";
import { toast } from "react-toastify";

const travelerTypes = [
  { type: "adults" },
  { type: "youths" },
  { type: "seniors" },
];

const commonInitialValues = {
  title: "",
  lead_traveler: false,
  first_name: "",
  last_name: "",
  date_of_birth: "",
};

const leaderInitialValues = {
  email_address: "",
  phone_number: "",
};

const trvlDocFields = {
  traveler_document: {
    country_code: "",
    document_number: "",
    expiration_date: "",
    doc_type: "",
  },
};

function createInitValues({
  travellersWithIds,
  defaultReqFields,
  travellers,
  agesArray,
  ageType,
  willHaveLeader,
}) {
  const travlrAgeType = ageType.toLowerCase();

  travellersWithIds
    .filter((trv) => trv.type === ageType)
    .map((traveller, idx) => {
      const age = agesArray && agesArray[idx] ? agesArray[idx] : 0;
      const travellerObj = {
        [travlrAgeType]: {
          ...traveller,
          ...commonInitialValues,
          ...(defaultReqFields.includes("passport") ? trvlDocFields : {}),
          ...(defaultReqFields.includes("country_of_residence")
            ? { country_of_residence: "" }
            : {}),
          ...(defaultReqFields.includes("place_of_birth")
            ? { country_of_birth: "" }
            : {}),
          ...(defaultReqFields.includes("reduction_card_id")
            ? { reduction_card_id: "" }
            : {}),
          ...(willHaveLeader && idx === 0
            ? { ...leaderInitialValues, lead_traveler: true }
            : {}),
          age,
          age_type: ageType,
        },
      };
      travellers[travlrAgeType + "s"].push(travellerObj[travlrAgeType]);
    });
}

export function getInitialValues({
  adults,
  youths,
  seniors,
  adults_ages,
  youths_ages,
  seniors_ages,
  defaultReqFields,
  travellersWithIds,
}) {
  var travellers = { adults: [], youths: [], seniors: [] };
  if (adults) {
    createInitValues({
      travellersWithIds,
      defaultReqFields,
      travellers,
      agesArray: adults_ages,
      ageType: "ADULT",
      willHaveLeader:
        travellersWithIds.filter((trv) => trv.type === "ADULT").length > 0,
    });
  }

  if (seniors) {
    createInitValues({
      travellersWithIds,
      defaultReqFields,
      travellers,
      agesArray: seniors_ages,
      ageType: "SENIOR",
      willHaveLeader:
        travellersWithIds.filter((trv) => trv.type === "ADULT").length === 0 &&
        travellersWithIds.filter((trv) => trv.type === "SENIOR").length > 0,
    });
  }

  if (youths) {
    createInitValues({
      travellersWithIds,
      defaultReqFields,
      travellers,
      agesArray: youths_ages,
      ageType: "YOUTH",
      willHaveLeader:
        travellersWithIds.filter((trv) => trv.type === "ADULT").length === 0 &&
        travellersWithIds.filter((trv) => trv.type === "SENIOR").length === 0,
    });
  }
  return travellers;
}

export function createBookPayload({
  search_session_id,
  prebookData,
  selectionData,
  values,
  market_type,
}) {
  const supplier = "raileurope";

  const billing_information = {
    agency: {
      name: "easy",
      email_address: "info@easytravel.tech",
      phone_number: "6969696969",
    },
  };

  const booking_id = _.get(prebookData, "booking_id", "");
  const api_uid = _.get(selectionData, "selectedType.goRoute.api_uid", "");
  const travelersNumber = _.get(
    prebookData,
    "booking_items.0.travelers",
    []
  ).length;

  var payload = {
    supplier: supplier,
    session_id: search_session_id,
    api_uid: api_uid,
    booking_id: booking_id,
    contact_details: {},
    travelers: [],
    billing_information: billing_information,
    market_type,
  };
  travelerTypes.forEach((travelerType) => {
    const travelers = values[travelerType.type];

    if (travelers.length > 0) {
      payload.travelers.push(
        ...travelers.map((traveler) => ({
          ...traveler,
          ...(traveler.traveler_document
            ? {
                traveler_document: {
                  ...traveler.traveler_document,
                  type: traveler.traveler_document.doc_type,
                  document_number: traveler.traveler_document.document_number,
                  country_code: traveler.traveler_document.country_code,
                  expiration_date:
                    traveler.traveler_document.expiration_date[0].toString(),
                },
              }
            : {}),
          date_of_birth: traveler.date_of_birth[0],
        }))
      );
    }
  });

  let leadTraveler = payload.travelers.find(
    (traveler) => traveler.lead_traveler
  );

  // payload.travelers.forEach((passenger, idx) => {
  //   passenger.id = travelersIds[idx].id;
  // });

  if (leadTraveler) {
    payload.contact_details = {
      id: leadTraveler.id,
      title: leadTraveler.title,
      first_name: leadTraveler.first_name,
      last_name: leadTraveler.last_name,
      email_address: leadTraveler.email_address,
      phone_number: leadTraveler.phone_number,
    };
  }
  if (travelersNumber === payload.travelers.length) {
    return payload;
  }
  return {};
}
export async function initPayment({ payload, dispatch }) {
  const validationSchema = yup.object().shape({
    customer_user_data: yup.object().shape({
      first_name: yup.string().required(),
      last_name: yup.string().required(),
      email: yup.string().email().required(),
      customer_uid: yup.string(),
      nationality: yup.string(),
    }),
    page_session_id: yup.string().required(),
    settedCurrency: yup.string().required(),
    amount: yup.number().required().moreThan(0),
    currency: yup.string().required(),
    reference: yup.string().required(),
  });

  try {
    await validationSchema.validate(payload, { abortEarly: false });
  } catch (error) {
    return false;
  }

  const newPrice = getExchangedPrice({
    price: payload.amount,
    priceCurrency: payload.currency,
    settedCurrency: payload.settedCurrency,
  });

  const booking_reference = payload.reference;
  const srv_type = "TRA";
  const order_id = `${srv_type}___${booking_reference}`;

  const base_url = window.location.origin;
  const success_url = `${base_url}/trains/successful-booking/${payload.page_session_id}`;
  const cancel_url = `${base_url}/trains/${payload.page_session_id}`;
  const fail_url = `${base_url}/failed-booking/${payload.page_session_id}`;

  sendPixelEvent("InitiateCheckout", {
    data: { product_type: "train" },
    withPageView: false,
  });

  const {
    first_name,
    last_name,
    guest_uuid: customer_uid,
    nationality,
    email,
    customer_stripe_id,
  } = payload.customer_user_data;

  dispatch(
    paymentDispatcher({
      booking_reference,
      page_session_id: payload.page_session_id,
      amount: newPrice,
      currency: payload.settedCurrency,
      first_name,
      last_name,
      email,
      customer_uid,
      nationality,
      customer_stripe_id,
      order_id,
      success_url,
      cancel_url,
      fail_url,
      srv_type,
      source_entity: payload.source_entity,
    })
  );
  return true;
}

export async function validateFn({ values, t, defaultReqFields }) {
  var outputErrors = new Set();
  try {
    await validationSchema({
      t,
      defaultReqFields,
    }).validate(values, { abortEarly: false });
  } catch (error) {
    if (error.errors) {
      outputErrors = new Set([...error.errors]);
    }
  }
  const combinedValues = [
    ...values.adults,
    ...values.seniors,
    ...values.youths,
  ];
  const seen = new Set();

  for (const value of combinedValues) {
    const key = value.first_name + value.last_name;
    if (key.length > 0 && seen.has(key)) {
      outputErrors.add(
        "Travelers with identical First name and Last name can cause problems with the carrier system. You might want to add the 1st letter of the traveler's middle name to the First name."
      );
    }
  }

  if (outputErrors.size > 0) {
    toast.warning(
      <ul>
        {[...outputErrors].map((error, idx) => (
          <li key={idx}>{error}</li>
        ))}
      </ul>
    );
  }

  return [...outputErrors];
}
